if (!customElements.get('olivers-market-hub')) {
  class OliversMarketHub extends HTMLElement {
    connectedCallback() {
      this.listContainer = this.querySelector('[data-hub-list]');
      this.mapContainer = this.querySelector('[data-hub-map]');
      this.countElement = this.querySelector('[data-hub-count]');
      
      this.searchInput = this.querySelector('[data-hub-search]');
      this.monthLabel = this.querySelector('[data-hub-month]');
      this.prevBtn = this.querySelector('[data-hub-prev]');
      this.nextBtn = this.querySelector('[data-hub-next]');
      this.tabs = this.querySelectorAll('[role="tab"]');
      
      this.overlayCard = this.querySelector('[data-hub-overlay]');

      this.availability = window.OliversPickupAvailability;
      this.rawLocations = this.readLocations();
      
      // Assign an index string "01", "02" to each unique location for the pins
      this.locations = this.rawLocations.map((loc, i) => {
        loc.index = String(i + 1).padStart(2, '0');
        return loc;
      });

      if (this.countElement) {
        this.countElement.textContent = this.locations.length;
      }
      
      this.map = null;
      this.markers = new Map();
      this.allDates = [];
      this.currentFilter = 'all'; // 'all' or 'week'
      this.searchQuery = '';
      this.activeItem = null; // { handle, date }

      this.initMap();
      this.buildDatesData();
      this.attachEvents();
      this.renderList();
    }

    readLocations() {
      const dataElement = this.querySelector('[data-hub-data]');
      if (!dataElement) return [];
      try {
        const parsed = JSON.parse(dataElement.textContent);
        return parsed.filter(l => l && l.active !== false && l.name);
      } catch (e) {
        console.error('Failed to parse market locations', e);
        return [];
      }
    }

    buildDatesData() {
      this.allDates = [];
      this.months = [];
      if (!this.availability) return;
      
      this.locations.forEach(location => {
        const dates = this.availability.availableDates(location, { limit: 12 });
        dates.forEach(date => {
          this.allDates.push({ date, location });
        });
      });
      this.allDates.sort((a, b) => a.date.localeCompare(b.date));

      // Build unique months
      this.allDates.forEach(item => {
        const d = this.availability.parseDate(item.date);
        const monthKey = d.toLocaleString('de-CH', { month: 'long', year: 'numeric', timeZone: 'UTC' });
        const monthSort = item.date.slice(0, 7);
        if (!this.months.find(m => m.sort === monthSort)) {
          this.months.push({ key: monthKey, sort: monthSort });
        }
      });
      this.currentMonthIndex = 0;
    }

    attachEvents() {
      // Month Nav
      if (this.prevBtn && this.nextBtn) {
        this.prevBtn.addEventListener('click', () => {
          if (this.currentMonthIndex > 0) {
            this.currentMonthIndex--;
            this.renderList();
          }
        });
        this.nextBtn.addEventListener('click', () => {
          if (this.currentMonthIndex < this.months.length - 1) {
            this.currentMonthIndex++;
            this.renderList();
          }
        });
      }

      // Tabs (view toggles)
      this.tabs.forEach(tab => {
        tab.addEventListener('click', () => {
          this.tabs.forEach(t => t.setAttribute('aria-selected', 'false'));
          tab.setAttribute('aria-selected', 'true');
          this.currentFilter = tab.dataset.tab;
          this.renderList();
        });
      });

      // Search
      if (this.searchInput) {
        this.searchInput.addEventListener('input', (e) => {
          this.searchQuery = e.target.value.toLowerCase();
          this.renderList();
        });
      }
    }

    async initMap() {
      if (!this.mapContainer || !window.L) return;

      try {
        const tileUrl = this.dataset.tileUrl || 'https://tile.openstreetmap.org/{z}/{x}/{y}.png';
        const attribution = this.dataset.attribution || '';
        
        this.map = L.map(this.mapContainer, { scrollWheelZoom: false, zoomControl: false });
        L.control.zoom({ position: 'topleft' }).addTo(this.map);
        L.tileLayer(tileUrl, { attribution }).addTo(this.map);

        // Close overlay when clicking on the map background
        this.map.on('click', () => {
          this.closeOverlay();
        });

        const bounds = [];
        
        this.locations.forEach(location => {
          if (location.latitude && location.longitude) {
            const lat = parseFloat(location.latitude);
            const lng = parseFloat(location.longitude);
            if (!isNaN(lat) && !isNaN(lng)) {
              bounds.push([lat, lng]);
              
              const iconHtml = `<div class="custom-map-marker"><span>${location.index}</span></div>`;
              const customIcon = L.divIcon({
                html: iconHtml,
                className: '',
                iconSize: [32, 32],
                iconAnchor: [16, 32]
              });

              const marker = L.marker([lat, lng], { icon: customIcon }).addTo(this.map);
              
              marker.on('click', (e) => {
                L.DomEvent.stopPropagation(e);
                const upcoming = this.allDates.find(d => d.location.handle === location.handle);
                this.setActive(location.handle, upcoming ? upcoming.date : null);
              });

              this.markers.set(location.handle, marker);
            }
          }
        });

        if (bounds.length > 0) {
          this.map.fitBounds(bounds, { padding: [50, 50] });
        } else {
          this.map.setView([47.3769, 8.5417], 10);
        }
      } catch (e) {
        console.error('Failed to init map', e);
      }
    }

    getFilteredDates() {
      let filtered = [...this.allDates];

      if (this.searchQuery) {
        filtered = filtered.filter(item => 
          item.location.name.toLowerCase().includes(this.searchQuery) || 
          item.location.address.toLowerCase().includes(this.searchQuery)
        );
      }

      if (this.currentFilter === 'week') {
        const today = new Date();
        const nextWeek = new Date();
        nextWeek.setDate(today.getDate() + 7);
        const nextWeekIso = nextWeek.toISOString().slice(0, 10);
        
        filtered = filtered.filter(item => item.date <= nextWeekIso);
      } else if (this.months && this.months.length > 0) {
        const activeMonth = this.months[this.currentMonthIndex].sort;
        filtered = filtered.filter(item => item.date.startsWith(activeMonth));
      }

      return filtered;
    }

    renderList() {
      if (this.months && this.months.length > 0) {
        if (this.monthLabel) this.monthLabel.textContent = this.months[this.currentMonthIndex].key;
        if (this.prevBtn) this.prevBtn.disabled = this.currentMonthIndex === 0;
        if (this.nextBtn) this.nextBtn.disabled = this.currentMonthIndex === this.months.length - 1;
      }

      const datesToRender = this.getFilteredDates();
      this.listContainer.innerHTML = '';

      if (datesToRender.length === 0) {
        this.listContainer.innerHTML = `<div style="padding: 32px; text-align: center; opacity: 0.7;">Keine Termine gefunden.</div>`;
        return;
      }

      // Group by Day (e.g. FR · 21 AUG)
      const grouped = {};
      datesToRender.forEach(item => {
        const d = this.availability.parseDate(item.date);
        const weekday = d.toLocaleString('de-CH', { weekday: 'short', timeZone: 'UTC' }).toUpperCase();
        const day = d.getUTCDate();
        const monthShort = d.toLocaleString('de-CH', { month: 'short', timeZone: 'UTC' }).toUpperCase();
        
        const key = `${weekday} · ${String(day).padStart(2, '0')} ${monthShort}`;
        if (!grouped[key]) grouped[key] = [];
        grouped[key].push(item);
      });

      let html = '';
      Object.keys(grouped).forEach(dayLabel => {
        html += `<div class="olivers-market-hub__day-group">
          <div class="olivers-market-hub__day-title">${dayLabel}</div>`;
        
        grouped[dayLabel].forEach(item => {
          const isActive = this.activeItem && this.activeItem.handle === item.location.handle && this.activeItem.date === item.date;
          const address = item.location.address || '';
          const addressLine = address.split('\n')[0];
          
          html += `
            <div class="olivers-market-hub__date-row" 
                 data-handle="${item.location.handle}"
                 data-date="${item.date}"
                 ${isActive ? 'data-active="true"' : ''}>
              <div class="olivers-market-hub__number-circle">${item.location.index}</div>
              <div class="olivers-market-hub__row-content">
                <div class="olivers-market-hub__row-title">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>
                  ${item.location.name}
                </div>
                <div class="olivers-market-hub__row-address">${addressLine}</div>
              </div>
              <div class="olivers-market-hub__row-time">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
                ${item.location.hours || ''}
              </div>
              <div class="olivers-market-hub__row-arrow">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"/><polyline points="12 16 16 12 12 8"/><line x1="8" y1="12" x2="16" y2="12"/></svg>
              </div>
            </div>
          `;
        });
        html += `</div>`;
      });
      
      this.listContainer.innerHTML = html;

      // Attach clicks
      const rows = this.listContainer.querySelectorAll('.olivers-market-hub__date-row');
      rows.forEach(row => {
        row.addEventListener('click', () => {
          this.setActive(row.dataset.handle, row.dataset.date);
        });
      });
    }

    setActive(handle, date) {
      this.activeItem = { handle, date };
      
      // Update list UI
      const rows = this.listContainer.querySelectorAll('.olivers-market-hub__date-row');
      rows.forEach(row => {
        if (row.dataset.handle === handle && row.dataset.date === date) {
          row.setAttribute('data-active', 'true');
        } else {
          row.removeAttribute('data-active');
        }
      });

      // Update Map Markers
      this.markers.forEach((marker, mHandle) => {
        const iconEl = marker.getElement().querySelector('.custom-map-marker');
        if (iconEl) {
          if (mHandle === handle) {
            iconEl.classList.add('active');
            marker.setZIndexOffset(1000);
          } else {
            iconEl.classList.remove('active');
            marker.setZIndexOffset(0);
          }
        }
      });

      // Center map slightly
      if (this.map && this.markers.has(handle)) {
        const marker = this.markers.get(handle);
        this.map.panTo(marker.getLatLng());
      }

      this.openOverlay(handle, date);
    }

    openOverlay(handle, dateStr) {
      if (!this.overlayCard) return;
      const location = this.locations.find(l => l.handle === handle);
      if (!location) return;

      this.overlayCard.querySelector('[data-overlay-number]').textContent = location.index;
      this.overlayCard.querySelector('[data-overlay-title]').textContent = location.name;
      this.overlayCard.querySelector('[data-overlay-address]').innerHTML = location.address.replace(/\n/g, '<br>');
      this.overlayCard.querySelector('[data-overlay-time]').textContent = location.hours;
      
      const dateEl = this.overlayCard.querySelector('[data-overlay-date]');
      if (dateStr && this.availability) {
        dateEl.textContent = this.availability.formatDate(dateStr);
      } else {
        dateEl.textContent = 'Mehrere Termine';
      }

      // Map link
      const mapLink = this.overlayCard.querySelector('[data-overlay-map-link]');
      if (mapLink && this.map && location.latitude) {
        mapLink.onclick = () => {
          this.map.setView([location.latitude, location.longitude], 14);
        };
      }

      // Route link
      const routeLink = this.overlayCard.querySelector('[data-overlay-route-link]');
      if (routeLink && location.latitude) {
        routeLink.href = `https://www.google.com/maps/search/?api=1&query=${location.latitude},${location.longitude}`;
      }

      const orderLink = this.overlayCard.querySelector('[data-overlay-order-link]');
      if (orderLink) {
        const orderUrl = new URL(this.dataset.orderUrl || '/collections/cheesecakes', window.location.origin);
        orderUrl.searchParams.set('pickup_market', location.handle);
        orderLink.href = `${orderUrl.pathname}${orderUrl.search}${orderUrl.hash}`;
        orderLink.setAttribute('aria-label', (this.dataset.orderAria || '[market]').replaceAll('[market]', location.name));
      }

      this.overlayCard.classList.add('visible');
    }

    closeOverlay() {
      this.activeItem = null;
      if (this.overlayCard) this.overlayCard.classList.remove('visible');
      
      // Remove active state from list
      const rows = this.listContainer.querySelectorAll('.olivers-market-hub__date-row');
      rows.forEach(row => row.removeAttribute('data-active'));

      // Remove active state from markers
      this.markers.forEach(marker => {
        const iconEl = marker.getElement().querySelector('.custom-map-marker');
        if (iconEl) iconEl.classList.remove('active');
      });
    }
  }

  customElements.define('olivers-market-hub', OliversMarketHub);
}
